/**
 * User Role Helper Functions - New Architecture
 *
 * These helpers provide a clean API for checking user types and roles
 * using the new user_type + roles architecture.
 */

import { User } from '../types/user';

/**
 * Check if user is a client account
 */
export function isClient(user: User | null | undefined): boolean {
  if (!user) return false;
  return user.user_type === 'client';
}

/**
 * Check if user is a staff account
 */
export function isStaff(user: User | null | undefined): boolean {
  if (!user) return false;
  return user.user_type === 'staff';
}

/**
 * Check if user has admin role
 */
export function isAdmin(user: User | null | undefined): boolean {
  if (!user) return false;
  return user.roles?.includes('admin') ?? false;
}

/**
 * Check if user has coach role
 */
export function isCoach(user: User | null | undefined): boolean {
  if (!user) return false;
  return user.roles?.includes('coach') ?? false;
}

/**
 * Check if user has any of the specified roles
 */
export function hasRole(user: User | null | undefined, roles: string | string[]): boolean {
  if (!user) return false;
  const roleArray = Array.isArray(roles) ? roles : [roles];
  return roleArray.some(role => user.roles?.includes(role));
}

/**
 * Get user's account type for display
 */
export function getUserType(user: User | null | undefined): 'client' | 'staff' {
  if (!user) return 'client';
  return user.user_type || 'client';
}

/**
 * Get user's roles array (empty for clients)
 */
export function getUserRoles(user: User | null | undefined): string[] {
  if (!user) return [];
  return user.roles || [];
}

/**
 * Check if user is the built-in system admin account
 * This is the only account that should be protected from deactivation/deletion
 */
export function isBuiltInAdmin(user: User | null | undefined): boolean {
  if (!user) return false;
  // Built-in admin ID from initial system setup
  return user.id === 'staff-e1be4d16-ddbd-4745-96fb-33de288d19c1';
}
